import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { DatepickerHeaderComponent } from '@blaze-shared/shared-feature';
import { AutoSlashDateInputDirective } from '@blaze-shared/shared-ui';
import { DATE_FORMAT_TO_FNS_MAP, isNullish, isValidDate } from '@blaze-shared/shared-util';

import { DateFnsAdapter } from '../../../../datetime-input/custom-date-fns-adapter';
import { DynamicFormatDate } from '../../../../datetime-input/dynamic-format-date';
import { prepareDateToOutput, transformStringDateToDate } from '../../../availability.utils';

@Component({
  selector: 'blaze-availability-date-picker',
  templateUrl: './availability-date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, AutoSlashDateInputDirective, MatMenuModule, MatDatepickerModule, MatIcon],
  providers: [
    DynamicFormatDate,
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useExisting: DynamicFormatDate,
    },
  ],
})
export class AvailabilityDatePickerComponent implements AfterViewInit, OnDestroy {
  private readonly dynamicFormatDate = inject(DynamicFormatDate);

  value = input<string | null>();
  min = input<string | null>();
  max = input<string | null>();
  placeholder = input<string>();

  pickerOpened = output();
  pickerClosed = output();
  valueChanged = output<string | null>();

  dateInput = viewChild('dateInput', { read: ElementRef<HTMLInputElement> });
  datepicker = viewChild<MatDatepicker<Date>>('datepicker');

  private readonly dateValue = computed<Date | null | undefined>(() => {
    const value = this.value();
    if (value === null || value === undefined || !value) {
      return null;
    }

    if (typeof value === 'string') {
      return transformStringDateToDate(value);
    }
    throw new TypeError(
      `CellDateComponent: value is expected to be string | null | undefined, got ${typeof value}`,
    );
  });

  protected datepickerHeader = DatepickerHeaderComponent;
  protected currentDateFormat = DATE_FORMAT_TO_FNS_MAP['MM/DD/YYYY'];

  protected internalValue = linkedSignal<Date | null>(() => {
    const dateValue = this.dateValue();
    return dateValue ? new Date(dateValue) : null;
  });

  constructor() {
    this.dynamicFormatDate.setDisplayFormat({ dateInput: this.currentDateFormat });
  }

  ngAfterViewInit(): void {
    const datepicker = this.datepicker();
    if (!datepicker) {
      throw new Error('Expected datepicker to be defined');
    }
  }

  ngOnDestroy(): void {
    this.closeDatePicker();
  }

  protected openDatePicker(): void {
    const datepicker = this.datepicker();
    if (!datepicker) {
      throw new Error('Expected datepicker to be defined');
    }
    if (datepicker.opened) {
      return;
    }
    datepicker.open();

    setTimeout(() => {
      if (!datepicker.opened) {
        return;
      }

      const datepickerHtml = document.getElementById(datepicker.id);
      const datepickerContentHtml = datepickerHtml?.closest('mat-datepicker-content');

      if (datepickerContentHtml) {
        datepickerContentHtml.classList.add('b-dti');
      }
    });
  }

  protected closeDatePicker(): void {
    this.datepicker()?.close();
  }

  protected onPickerClosed(): void {
    const value = this.dateInput()?.nativeElement.value;
    if (!value) {
      this.internalValue.set(null);
      this.valueChanged.emit(null);
    }
    this.pickerClosed.emit();
  }

  protected onDateChange(value: Date | null): void {
    if (isNullish(value)) {
      this.internalValue.set(null);
      this.valueChanged.emit(null);
      return;
    }

    const inputValue = this.dateInput()?.nativeElement.value ?? '';
    if (!inputValue.split('/')[2] && inputValue.length > 0) {
      return;
    }

    let newValue: Date | null = null;
    if (value instanceof Date) {
      newValue = transformStringDateToDate(prepareDateToOutput(value));
    } else {
      throw new TypeError(
        `InlineDateComponent: value is expected to be Date | null, got ${typeof value}`,
      );
    }

    if (!newValue || !isValidDate(newValue)) {
      this.internalValue.set(null);
      this.valueChanged.emit(null);
      return;
    }

    if (document.activeElement !== this.dateInput()?.nativeElement) {
      this.internalValue.set(newValue);
    }
    this.valueChanged.emit(prepareDateToOutput(value));
  }
}
